import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ApiError,
  fetchProductAccess,
  ROLE_NONE,
  updateProductAccess,
  type ProductAccessBrandItem,
} from "@/lib/product-access-api";

type ProductAccessSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  membershipId: number;
  memberLabel: string;
};

type DraftTool = { key: string; role: string };
type DraftBrand = { id: number; role: string; tools: Record<string, DraftTool> };

function roleToSelect(role: string | null | undefined) {
  return role && role !== ROLE_NONE ? role : ROLE_NONE;
}

function selectToApiRole(value: string): string | null {
  return value === ROLE_NONE ? null : value;
}

export function ProductAccessSheet({
  open,
  onOpenChange,
  accountId,
  membershipId,
  memberLabel,
}: ProductAccessSheetProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [brands, setBrands] = useState<ProductAccessBrandItem[]>([]);
  const [draft, setDraft] = useState<Record<number, DraftBrand>>({});

  useEffect(() => {
    if (!open) return;

    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const payload = await fetchProductAccess(accountId, membershipId);
        if (cancelled) return;
        setBrands(payload.brands);
        setDraft(
          Object.fromEntries(
            payload.brands.map((b) => [
              b.id,
              {
                id: b.id,
                role: roleToSelect(b.role),
                tools: Object.fromEntries(
                  (b.tools ?? []).map((t) => [
                    t.key,
                    { key: t.key, role: roleToSelect(t.role) },
                  ]),
                ),
              },
            ]),
          ),
        );
      } catch (error) {
        if (cancelled) return;
        const message = error instanceof ApiError ? error.message : "加载产品授权失败";
        toast.error(message);
        onOpenChange(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [open, accountId, membershipId, onOpenChange]);

  function setBrandRole(brandId: number, role: string) {
    setDraft((prev) => {
      const current = prev[brandId];
      if (!current) return prev;
      const nextTools =
        role === ROLE_NONE
          ? Object.fromEntries(
              Object.values(current.tools).map((t) => [t.key, { ...t, role: ROLE_NONE }]),
            )
          : current.tools;
      return { ...prev, [brandId]: { ...current, role, tools: nextTools } };
    });
  }

  function setToolRole(brandId: number, toolKey: string, role: string) {
    setDraft((prev) => {
      const current = prev[brandId];
      if (!current) return prev;
      const tool = current.tools[toolKey] ?? { key: toolKey, role: ROLE_NONE };
      return {
        ...prev,
        [brandId]: {
          ...current,
          tools: { ...current.tools, [toolKey]: { ...tool, role } },
        },
      };
    });
  }

  async function handleSave() {
    const body = brands.map((b) => {
      const row = draft[b.id];
      const brandRole = selectToApiRole(row?.role ?? ROLE_NONE);
      return {
        id: b.id,
        role: brandRole,
        tools:
          brandRole == null
            ? []
            : (b.tools ?? []).map((t) => ({
                key: t.key,
                role: selectToApiRole(row?.tools[t.key]?.role ?? ROLE_NONE),
              })),
      };
    });

    setSaving(true);
    try {
      const payload = await updateProductAccess(accountId, membershipId, body);
      setBrands(payload.brands);
      toast.success("产品授权已保存");
      onOpenChange(false);
    } catch (error) {
      const message = error instanceof ApiError ? error.message : "保存产品授权失败";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(85vh,720px)] w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="shrink-0 border-b border-border/70 px-6 py-4 text-left">
          <DialogTitle>产品授权 · {memberLabel}</DialogTitle>
          <DialogDescription>
            按品牌设置成员角色；品牌下仅显示已开通的工具。品牌选「无」将清除该品牌全部工具授权
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-6 py-4">
          {loading ? (
            <p className="text-sm text-muted-foreground">加载中…</p>
          ) : brands.length === 0 ? (
            <p className="text-sm text-muted-foreground">暂无品牌。请先在企业下创建品牌。</p>
          ) : (
            brands.map((brand) => {
              const row = draft[brand.id];
              const brandRole = row?.role ?? ROLE_NONE;
              const toolsEditable = brandRole !== ROLE_NONE;

              return (
                <div key={brand.id} className="rounded-lg border border-border/70 p-3">
                  <div className="space-y-1.5">
                    <p className="text-sm font-medium text-foreground">{brand.name}</p>
                    <Label htmlFor={`brand-role-${brand.id}`} className="text-xs text-muted-foreground">
                      品牌角色
                    </Label>
                    <Select
                      value={brandRole}
                      disabled={saving}
                      onValueChange={(value) => setBrandRole(brand.id, value)}
                    >
                      <SelectTrigger id={`brand-role-${brand.id}`} className="h-8 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ROLE_NONE}>无</SelectItem>
                        {brand.roles.map((role) => (
                          <SelectItem key={role.value} value={role.value}>
                            {role.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {(brand.tools?.length ?? 0) > 0 ? (
                    <div className="mt-3 space-y-2 border-t border-border/60 pt-3">
                      <p className="text-xs font-medium text-muted-foreground">已开通工具</p>
                      {brand.tools.map((tool) => {
                        const toolRole = row?.tools[tool.key]?.role ?? ROLE_NONE;
                        return (
                          <div key={tool.key} className="space-y-1">
                            <Label
                              htmlFor={`tool-${brand.id}-${tool.key}`}
                              className="text-xs text-foreground"
                            >
                              {tool.name}
                            </Label>
                            <Select
                              value={toolRole}
                              disabled={!toolsEditable || saving}
                              onValueChange={(value) => setToolRole(brand.id, tool.key, value)}
                            >
                              <SelectTrigger
                                id={`tool-${brand.id}-${tool.key}`}
                                className="h-8 w-full"
                              >
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={ROLE_NONE}>无</SelectItem>
                                {tool.roles.map((role) => (
                                  <SelectItem key={role.value} value={role.value}>
                                    {role.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="mt-3 text-xs text-muted-foreground">
                      该品牌尚未开通工具。请到品牌面板开通 AISO / 客户旅程等。
                    </p>
                  )}
                </div>
              );
            })
          )}
        </div>

        <DialogFooter className="shrink-0 border-t border-border/70 px-6 py-4 sm:justify-end">
          <Button type="button" variant="outline" disabled={saving} onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button type="button" disabled={loading || saving} onClick={() => void handleSave()}>
            {saving ? "保存中…" : "保存"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
